// Legacy per-AI-Agent troubleshooting flow — agent list → session list →
// session detail. Pre-dates the unified Call-search / CallDetail flow
// but still useful when you have an agentId in hand.

import React, { useCallback, useEffect, useState } from 'react';
import {
  ActionIcon,
  Anchor,
  Badge,
  Button,
  Code,
  Grid,
  Group,
  Loader,
  Paper,
  ScrollArea,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import {
  IconPhone,
  IconRefresh,
  IconSparkles,
} from '@tabler/icons-react';

import { callTool } from '../../api_scripts/wxccMcpClient';
import { Breadcrumbs, ErrorAlert, StateBadge, fmtTs, short } from './utils';
import { AuditPanel, MessageList, SessionHeader, AiSummarySection, TimelinePanel } from './sessionPanels';

export function AgentTroubleshooting({ setView }) {
  const [state, setState] = useState({ loading: true, error: null, agents: [], total: 0 });

  const load = useCallback(async () => {
    setState({ loading: true, error: null, agents: [], total: 0 });
    try {
      const res = await callTool('ops', 'wxcc-operations-list-ai-agents', { view: 'list' });
      setState({
        loading: false,
        error: null,
        agents: res.agents || [],
        total: res.matchedCount ?? (res.agents || []).length,
      });
    } catch (e) {
      setState({ loading: false, error: e.message, agents: [], total: 0 });
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={3}>AI Agents</Title>
        <Group gap="xs">
          <Text size="xs" c="dimmed">
            {state.total} agents
          </Text>
          <Tooltip label="Refresh">
            <ActionIcon variant="subtle" onClick={load}>
              <IconRefresh size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>

      {state.loading && <Loader size="sm" />}
      {state.error && <ErrorAlert message={state.error} />}

      {!state.loading && !state.error && (
        <Grid gutter="md">
          {state.agents.map((a) => (
            <Grid.Col key={a.id} span={{ base: 12, sm: 6, md: 4 }}>
              <Paper withBorder p="md" radius="md" style={{ height: '100%' }}>
                <Group justify="space-between" mb="xs">
                  <Text
                    fw={500}
                    style={{ cursor: 'pointer' }}
                    onClick={() => setView({ name: 'sessions', agentId: a.id, agentName: a.name })}
                  >
                    {a.name}
                  </Text>
                  {a.disabled && (
                    <Badge size="xs" color="gray">
                      disabled
                    </Badge>
                  )}
                </Group>
                <Group gap="xs">
                  {a.botType && (
                    <Badge size="xs" variant="light">
                      {a.botType}
                    </Badge>
                  )}
                  {a.defaultLanguage && (
                    <Badge size="xs" variant="light" color="gray">
                      {a.defaultLanguage}
                    </Badge>
                  )}
                </Group>
                <Text size="xs" c="dimmed" mt="xs" style={{ fontFamily: 'monospace' }}>
                  {a.id}
                </Text>
                <Group gap="xs" mt="sm">
                  <Button
                    size="xs"
                    variant="light"
                    onClick={() => setView({ name: 'sessions', agentId: a.id, agentName: a.name })}
                  >
                    Sessions
                  </Button>
                  <Button
                    size="xs"
                    variant="light"
                    color="violet"
                    leftSection={<IconSparkles size={12} />}
                    onClick={() => setView({ name: 'agent-tuning', agentId: a.id, agentName: a.name })}
                  >
                    Analyze tuning
                  </Button>
                </Group>
              </Paper>
            </Grid.Col>
          ))}
        </Grid>
      )}
    </Stack>
  );
}

export function SessionList({ agentId, agentName, setView }) {
  const [state, setState] = useState({ loading: true, error: null, sessions: [], total: 0 });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await callTool('ops', 'wxcc-operations-list-ai-agent-sessions', {
          agentId,
          view: 'list',
          limit: 20,
        });
        if (cancelled) return;
        setState({
          loading: false,
          error: null,
          sessions: res.sessions || [],
          total: res.matchedCount ?? (res.sessions || []).length,
        });
      } catch (e) {
        if (!cancelled) setState({ loading: false, error: e.message, sessions: [], total: 0 });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [agentId]);

  return (
    <Stack>
      <Breadcrumbs
        items={[
          { label: 'AI Agents', onClick: () => setView({ name: 'ai-agents' }) },
          { label: agentName || agentId },
        ]}
      />
      <Group justify="space-between">
        <div>
          <Title order={3}>Sessions</Title>
          <Text size="sm" c="dimmed">
            Most recent 20 sessions for this agent (tool cap). {state.total.toLocaleString()} total.
          </Text>
        </div>
      </Group>

      {state.loading && <Loader size="sm" />}
      {state.error && <ErrorAlert message={state.error} />}

      {!state.loading && !state.error && (
        <Paper withBorder radius="md">
          <ScrollArea>
            <Table verticalSpacing="xs" highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Session</Table.Th>
                  <Table.Th>Interaction</Table.Th>
                  <Table.Th>State</Table.Th>
                  <Table.Th>Started</Table.Th>
                  <Table.Th>Updated</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {state.sessions.map((s) => (
                  <Table.Tr
                    key={s.sessionId}
                    style={{ cursor: 'pointer' }}
                    onClick={() => setView({ name: 'session', agentId, agentName, sessionId: s.sessionId })}
                  >
                    <Table.Td style={{ fontFamily: 'monospace', fontSize: 12 }}>
                      <Anchor size="xs">{short(s.sessionId)}</Anchor>
                      {s.isTest && (
                        <Badge size="xs" color="gray" variant="outline" ml={6}>
                          test
                        </Badge>
                      )}
                    </Table.Td>
                    <Table.Td style={{ fontFamily: 'monospace', fontSize: 12 }} c="dimmed">
                      {short(s.interactionId) || <em>null</em>}
                    </Table.Td>
                    <Table.Td>
                      <StateBadge state={s.state} />
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs">{fmtTs(s.createdAt)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs">{fmtTs(s.updatedAt)}</Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        </Paper>
      )}
    </Stack>
  );
}

export function SessionDetail({ agentId, agentName, sessionId, setView }) {
  const [sess, setSess] = useState({ loading: true, error: null, session: null });
  const [msgs, setMsgs] = useState({ loading: true, error: null, data: null });
  const [audit, setAudit] = useState({ loading: true, error: null, data: null });
  const [timeline, setTimeline] = useState({ loading: false, error: null, data: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Load session first — need interactionId for the timeline.
      try {
        const res = await callTool('ops', 'wxcc-operations-get-ai-agent-session', { agentId, sessionId });
        if (cancelled) return;
        setSess({ loading: false, error: null, session: res.session || res });
        const iid = res.session?.interactionId ?? res.interactionId;
        if (iid) {
          setTimeline({ loading: true, error: null, data: null });
          try {
            const t = await callTool('ops', 'wxcc-operations-get-contact-timeline', { interactionId: iid });
            if (!cancelled) setTimeline({ loading: false, error: null, data: t });
          } catch (e) {
            if (!cancelled) setTimeline({ loading: false, error: e.message, data: null });
          }
        }
      } catch (e) {
        if (!cancelled) setSess({ loading: false, error: e.message, session: null });
      }
    })();
    (async () => {
      try {
        const res = await callTool('ops', 'wxcc-operations-get-ai-agent-session-messages', {
          agentId,
          sessionId,
          limit: 50,
        });
        if (!cancelled) setMsgs({ loading: false, error: null, data: res });
      } catch (e) {
        if (!cancelled) setMsgs({ loading: false, error: e.message, data: null });
      }
    })();
    (async () => {
      try {
        const res = await callTool('ops', 'wxcc-operations-audit-ai-agent', {
          agentId,
          sessionId,
          scope: 'exactSession',
        });
        if (!cancelled) setAudit({ loading: false, error: null, data: res });
      } catch (e) {
        if (!cancelled) setAudit({ loading: false, error: e.message, data: null });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [agentId, sessionId]);

  const interactionId = sess.session?.interactionId ?? null;

  return (
    <Stack>
      <Breadcrumbs
        items={[
          { label: 'AI Agents', onClick: () => setView({ name: 'ai-agents' }) },
          { label: agentName || agentId, onClick: () => setView({ name: 'sessions', agentId, agentName }) },
          { label: short(sessionId) },
        ]}
      />

      <Group justify="space-between" align="flex-start">
        <div>
          <Title order={3}>Session detail</Title>
          <Text size="xs" c="dimmed" style={{ fontFamily: 'monospace' }}>
            {sessionId}
          </Text>
          {interactionId && (
            <Text size="xs" c="dimmed">
              interactionId <Code>{interactionId}</Code>
            </Text>
          )}
        </div>
        {interactionId && (
          <Button
            size="xs"
            variant="light"
            leftSection={<IconPhone size={14} />}
            onClick={() => {
              // Default to a ±12h window around the session's createdAt.
              const anchor = sess.session?.createdAt
                ? (typeof sess.session.createdAt === 'number'
                    ? sess.session.createdAt
                    : Date.parse(sess.session.createdAt))
                : Date.now();
              const twelveHours = 12 * 60 * 60 * 1000;
              setView({
                name: 'call-detail',
                interactionId,
                window: { from: anchor - twelveHours, to: anchor + twelveHours },
                cachedAiSessions: null,
              });
            }}
          >
            View full call context
          </Button>
        )}
      </Group>

      <SessionHeader loading={sess.loading} error={sess.error} session={sess.session} />
      <AiSummarySection agentId={agentId} sessionId={sessionId} />
      <AuditPanel loading={audit.loading} error={audit.error} data={audit.data} />

      <Grid gutter="md">
        <Grid.Col span={{ base: 12, lg: 6 }}>
          <Title order={5} mb="xs">
            Turn-by-turn (AI side)
          </Title>
          <MessageList loading={msgs.loading} error={msgs.error} data={msgs.data} />
        </Grid.Col>
        <Grid.Col span={{ base: 12, lg: 6 }}>
          <Title order={5} mb="xs">
            Contact timeline (routing side)
          </Title>
          <TimelinePanel
            interactionId={interactionId}
            loading={timeline.loading}
            error={timeline.error}
            data={timeline.data}
          />
        </Grid.Col>
      </Grid>
    </Stack>
  );
}
